import type { SignatureRequestsBulkResponse } from './types';

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchSignatureRequestsBulk } from './fetchSignatureRequestsBulk';

/**
 * React Query based custom hook for fetching signature requests for multiple delivery notes.
 *
 * @param deliveryNoteIds - The IDs of the delivery notes. If empty, returns all signature requests.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQuerySignatureRequestsBulk = (
  deliveryNoteIds: UUID[] = [],
  options?: Omit<
    UseQueryOptions<SignatureRequestsBulkResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    enabled: Boolean(deliveryNoteIds?.length),
    meta: {
      suppressErrorForErrorCodes: [422],
    },
    queryFn: async () => fetchSignatureRequestsBulk(deliveryNoteIds),
    queryKey: queryKeysDeliveryNote.getSignatureRequestsBulk(deliveryNoteIds),
    throwOnError: false,
    ...options,
  });
};
